import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Heart, MapPin, GraduationCap, Calendar, DollarSign, Clock, CheckCircle, XCircle, Eye, Filter, Play, Image as ImageIcon } from 'lucide-react';
import { useGetSponsoredChildrenQuery, useUpdateSponsorshipMutation, useGetUserSponsorshipStatsQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';

const MySponsoredChildren: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'paused' | 'cancelled' | 'completed'>('all');
  const [selectedSponsorship, setSelectedSponsorship] = useState<any>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateAction, setUpdateAction] = useState<'pause' | 'resume' | 'cancel' | 'auto_renewal'>('pause');
  const [reason, setReason] = useState('');
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const { data: sponsorshipsResponse, isLoading } = useGetSponsoredChildrenQuery({
    page: 1,
    limit: 50,
    status: statusFilter === 'all' ? undefined : statusFilter,
  }, {
    skip: !user,
  });

  const { data: statsResponse } = useGetUserSponsorshipStatsQuery(undefined, {
    skip: !user,
  });

  const [updateSponsorship] = useUpdateSponsorshipMutation();

  const sponsorships = sponsorshipsResponse?.data?.sponsorships || [];
  const stats = statsResponse?.data;

  const formatCurrency = (amount: number, symbol: string) => {
    return `${symbol}${amount.toLocaleString('en-US')}`;
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Active
          </Badge>
        );
      case 'paused':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Paused
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="danger" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Cancelled
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="info" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Completed
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleUpdateSponsorship = async () => {
    if (!selectedSponsorship) return;

    setIsUpdating(true);
    try {
      const updateData: any = {
        sponsorship_id: selectedSponsorship.sponsorship_id,
      };

      if (updateAction === 'auto_renewal') {
        updateData.auto_renewal = autoRenewal;
      } else if (updateAction === 'pause') {
        updateData.status = 'paused';
        updateData.pause_reason = reason;
      } else if (updateAction === 'resume') {
        updateData.status = 'active';
      } else if (updateAction === 'cancel') {
        updateData.status = 'cancelled';
        updateData.cancellation_reason = reason;
      }

      const result = await updateSponsorship(updateData).unwrap();

      if (result?.success) {
        toast.success(result.data.message || 'Sponsorship updated successfully');
        setShowUpdateModal(false);
        setSelectedSponsorship(null);
        setReason('');
      } else {
        toast.error(result?.message || 'Failed to update sponsorship');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to update sponsorship. Please try again.');
      console.error('Update sponsorship error:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const openUpdateModal = (sponsorship: any, action: 'pause' | 'resume' | 'cancel' | 'auto_renewal') => {
    setSelectedSponsorship(sponsorship);
    setUpdateAction(action);
    setAutoRenewal(sponsorship.auto_renewal);
    setReason('');
    setShowUpdateModal(true);
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">My Sponsored Children</h1>
              <p className="text-gray-600">View and manage your child sponsorships</p>
            </div>
            <Button
              onClick={() => navigate('/sponsor/children')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Heart className="w-4 h-4" />
              Sponsor Another Child
            </Button>
          </div>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <p className="text-sm text-gray-600 mb-1">Total Sponsorships</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_sponsorships}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-gray-600 mb-1">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.active_sponsorships}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-gray-600 mb-1">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.total_amount_sponsored, '$')}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-gray-600 mb-1">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.sponsorships_by_type?.reduce((sum, item) => sum + item.count, 0) || 0}
              </p>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
            {(['all', 'active', 'paused', 'cancelled', 'completed'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </Card>

        {/* Sponsorships List */}
        {sponsorships.length === 0 ? (
          <Card className="p-12 text-center">
            <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Sponsored Children</h3>
            <p className="text-gray-600 mb-6">
              {statusFilter === 'all'
                ? "You haven't sponsored any children yet."
                : `No ${statusFilter} sponsorships found.`}
            </p>
            <Button onClick={() => navigate('/sponsor/children')}>Browse Children</Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sponsorships.map((sponsorship) => (
              <Card key={sponsorship.sponsorship_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* Child Image */}
                <div className="relative h-48 bg-gray-200">
                  <img
                    src={sponsorship.child.profile_image || 'https://via.placeholder.com/400x300?text=No+Image'}
                    alt={sponsorship.child.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
                    }}
                  />
                  <div className="absolute top-3 right-3">
                    {getStatusBadge(sponsorship.status)}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {sponsorship.child.name}
                  </h3>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span className="truncate">{sponsorship.child.location}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <GraduationCap className="w-4 h-4 mr-2" />
                      <span>{sponsorship.child.education_stage}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign className="w-4 h-4 mr-2" />
                      <span>
                        {formatCurrency(sponsorship.amount, '$')} / {sponsorship.sponsorship_type}
                      </span>
                    </div>
                    {sponsorship.next_payment_date && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>Next: {formatDate(sponsorship.next_payment_date)}</span>
                      </div>
                    )}
                    {sponsorship.auto_renewal && (
                      <div className="flex items-center text-sm text-green-600">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        <span>Auto-renewal enabled</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Link to={`/sponsor/child/${sponsorship.child.child_id}/updates`}>
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Updates
                      </Button>
                    </Link>
                    
                    {sponsorship.status === 'active' && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openUpdateModal(sponsorship, 'pause')}
                          className="flex-1 text-xs"
                        >
                          Pause
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openUpdateModal(sponsorship, 'auto_renewal')}
                          className="flex-1 text-xs"
                        >
                          {sponsorship.auto_renewal ? 'Disable Auto' : 'Enable Auto'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openUpdateModal(sponsorship, 'cancel')}
                          className="flex-1 text-xs text-red-600 hover:text-red-700"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                    {sponsorship.status === 'paused' && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openUpdateModal(sponsorship, 'resume')}
                          className="flex-1 text-xs"
                        >
                          Resume
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openUpdateModal(sponsorship, 'cancel')}
                          className="flex-1 text-xs text-red-600 hover:text-red-700"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Update Modal */}
      <Modal
        isOpen={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedSponsorship(null);
          setReason('');
        }}
        title={
          updateAction === 'pause' ? 'Pause Sponsorship' :
          updateAction === 'resume' ? 'Resume Sponsorship' :
          updateAction === 'cancel' ? 'Cancel Sponsorship' :
          'Update Auto-Renewal'
        }
      >
        {selectedSponsorship && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Child:</p>
              <p className="font-semibold text-gray-900">{selectedSponsorship.child.name}</p>
            </div>

            {updateAction === 'auto_renewal' && (
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRenewal}
                    onChange={(e) => setAutoRenewal(e.target.checked)}
                    className="w-4 h-4 text-gray-800 border-gray-300 rounded focus:ring-gray-500"
                  />
                  <span className="text-sm text-gray-700">Enable auto-renewal</span>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  When enabled, your sponsorship will automatically renew at the end of each period.
                </p>
              </div>
            )}

            {(updateAction === 'pause' || updateAction === 'cancel') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {updateAction === 'pause' ? 'Pause Reason' : 'Cancellation Reason'} (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder={`Please provide a reason for ${updateAction === 'pause' ? 'pausing' : 'cancelling'}...`}
                />
              </div>
            )}

            {updateAction === 'resume' && (
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-800">
                  Resuming this sponsorship will reactivate automatic payments. The next payment date will be adjusted based on the pause duration.
                </p>
              </div>
            )}

            {updateAction === 'cancel' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> Cancelling this sponsorship will make the child available for other sponsors. This action cannot be undone.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowUpdateModal(false);
                  setSelectedSponsorship(null);
                  setReason('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateSponsorship}
                disabled={isUpdating}
                className={`flex-1 ${
                  updateAction === 'cancel' ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-800 hover:bg-gray-900'
                }`}
              >
                {isUpdating ? 'Processing...' : 
                  updateAction === 'pause' ? 'Pause Sponsorship' :
                  updateAction === 'resume' ? 'Resume Sponsorship' :
                  updateAction === 'cancel' ? 'Cancel Sponsorship' :
                  'Update Settings'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default MySponsoredChildren;
